import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../../settings";
import {
  setFreeSpinTransitionActive,
  setCriticalAnimationPlaying,
  setFreeSpinActive,
  setFreeSpinTriggered,
  setUserInterfaceDisabled,
  setFreeSpinCounterVisible,
} from "../../../../State/actions";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Fixed pixel offset to fine-tune transition vertical alignment relative to the reelset
// Negative = push UP, Positive = push DOWN
const TRANSITION_Y_OFFSET = -8;

// Fixed pixel offset for the front mask glow layer (matches BACKGROUND_Y_OFFSET)
// Negative = push UP, Positive = push DOWN
const FRONT_MASK_Y_OFFSET = -158;

class FreeSpinSceneTransitionController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    device,
    orientation,
    dispatch = null,
  ) {
    this.container = container;
    this.spineData = {
      skeleton: "free_spin_transition-skeleton",
      atlas: "free_spin_transition-atlas",
    };
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.device = device;
    this.orientation = orientation;
    this.dispatch = dispatch;

    this.transitionSpine = null;
    this.frontMaskSpine = null;
    this.isVisible = false;
    this.isPlaying = false;

    this.targetScale = 1.0;
    this.calculateScale();

    this.initializeSpine();
    this.initializeFrontMask();
  }

  getSymbolScaleFactor() {
    if (!this.dimensions || !this.symbolWidth) return 1;
    return this.symbolWidth / Settings.SYMBOL_WIDTH;
  }

  calculateScale() {
    if (!this.dimensions || !this.symbolWidth || !this.symbolHeight) {
      this.targetScale = 1.0;
      return;
    }

    const symbolScaleFactor = this.getSymbolScaleFactor();
    this.targetScale = Math.max(0.3, Math.min(1.5, symbolScaleFactor));
  }

  resize(
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    device,
    orientation,
  ) {
    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;

    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const dividerDimensionsChanged =
      !this.dividerDimensions ||
      (dividerDimensions &&
        (this.dividerDimensions.width !== dividerDimensions.width ||
          this.dividerDimensions.height !== dividerDimensions.height));

    const deviceChanged =
      this.device !== device || this.orientation !== orientation;

    if (
      !symbolHeightsChanged &&
      !dimensionsChanged &&
      !dividerDimensionsChanged &&
      !deviceChanged
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.device = device;
    this.orientation = orientation;

    this.calculateScale();

    if (
      this.transitionSpine &&
      (symbolHeightsChanged || dimensionsChanged || dividerDimensionsChanged)
    ) {
      this.transitionSpine.x = 0;
      this.transitionSpine.y = TRANSITION_Y_OFFSET;
      this.transitionSpine.scale.set(this.targetScale);
    }

    if (
      this.frontMaskSpine &&
      (symbolHeightsChanged || dimensionsChanged || dividerDimensionsChanged)
    ) {
      this.frontMaskSpine.x = 0;
      this.frontMaskSpine.y = FRONT_MASK_Y_OFFSET;
      this.frontMaskSpine.scale.set(this.targetScale);
    }
  }

  initializeSpine() {
    try {
      this.transitionSpine = this.createSpine();
      if (!this.transitionSpine) return;

      if (!this.transitionSpine.skeleton || !this.transitionSpine.state) return;

      this.transitionSpine.x = 0;
      this.transitionSpine.y = TRANSITION_Y_OFFSET;
      this.transitionSpine.alpha = 0.001;
      this.transitionSpine.zIndex = 25;
      this.container.addChild(this.transitionSpine);
      this.transitionSpine.scale.set(this.targetScale);

      this.warmUp();
    } catch (error) {
      // Silent fail
    }
  }

  initializeFrontMask() {
    try {
      const assets = PIXI.Assets.cache;
      const hasSkeleton = assets.has("background_animation-skeleton");
      const hasAtlas = assets.has("background_animation-atlas");
      if (!hasSkeleton || !hasAtlas) {
        // Assets not loaded yet — retry after a short delay
        if (!this._frontMaskRetryCount) this._frontMaskRetryCount = 0;
        if (this._frontMaskRetryCount < 20) {
          this._frontMaskRetryCount++;
          this._frontMaskRetryTimeout = setTimeout(
            () => this.initializeFrontMask(),
            500,
          );
        }
        return;
      }

      this.frontMaskSpine = Spine.from({
        skeleton: "background_animation-skeleton",
        atlas: "background_animation-atlas",
      });

      if (!this.frontMaskSpine?.skeleton || !this.frontMaskSpine?.state) return;

      this.frontMaskSpine.x = 0;
      this.frontMaskSpine.y = FRONT_MASK_Y_OFFSET;
      this.frontMaskSpine.alpha = 1;
      this.frontMaskSpine.zIndex = 26;
      this.container.addChild(this.frontMaskSpine);
      this.frontMaskSpine.scale.set(this.targetScale);

      // Play front_mask looping — always visible
      const anims = this.frontMaskSpine.skeleton.data.animations.map(
        (a) => a.name,
      );
      if (anims.includes("front_mask")) {
        this.frontMaskSpine.state.setAnimation(0, "front_mask", true);
        this.frontMaskSpine.state.update(0.016);
        this.frontMaskSpine.skeleton.updateWorldTransform();
      }
    } catch (error) {
      // Silent fail
    }
  }

  warmUp() {
    if (!this.transitionSpine || !this.transitionSpine.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.transitionSpine.state.setAnimation(0, animations[0], false);
        this.transitionSpine.state.update(0.016);
        this.transitionSpine.skeleton.updateWorldTransform();
      }
      this.transitionSpine.alpha = 0.001;
    } catch (error) {}
  }

  createSpine() {
    try {
      if (!this.spineData) return null;

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.spineData.skeleton) ||
        !assets.has(this.spineData.atlas)
      ) {
        return null;
      }

      return Spine.from({
        skeleton: this.spineData.skeleton,
        atlas: this.spineData.atlas,
      });
    } catch (error) {
      return null;
    }
  }

  playAnimation() {
    if (
      !this.transitionSpine ||
      !this.transitionSpine.state ||
      this.isPlaying
    ) {
      return;
    }

    this.isPlaying = true;
    this.isVisible = true;
    this.transitionSpine.state.clearListeners();

    // Set animation while still hidden (alpha 0.001) to clear warmUp pose
    this.transitionSpine.state.setAnimation(0, "transition", false);

    // Play transition sound
    soundManager.play(SOUND_NAME.free_spin_transition);

    // Show on next frame — old pose is gone, new animation starts fresh
    requestAnimationFrame(() => {
      if (this.isPlaying) {
        this.transitionSpine.alpha = 1;
      }
    });

    // Add event + completion listener
    this.transitionSpine.state.addListener({
      event: (entry, event) => {
        // When reel_particles_start fires, notify via callback
        if (event.data.name === "reel_particles_start" && this.isPlaying) {
          if (this.onMidpoint) this.onMidpoint();
        }
        // When fs_counter_start fires, show the free spin counter
        if (event.data.name === "fs_counter_start" && this.isPlaying) {
          if (this.dispatch) {
            this.dispatch(setFreeSpinCounterVisible(true));
          }
        }
      },
      complete: (entry) => {
        if (entry.animation.name === "transition" && this.isPlaying) {
          this.transitionSpine.alpha = 0.001;
          this.isVisible = false;
          this.isPlaying = false;

          // Reset state so animation can be triggered again
          if (this.dispatch) {
            this.dispatch(setFreeSpinTransitionActive(false));
            this.dispatch(setFreeSpinTriggered(false));
            // this.dispatch(setFreeSpinActive(true));
            this.dispatch(setCriticalAnimationPlaying(false));
            this.dispatch(setUserInterfaceDisabled(false));
          }
        }
      },
    });
  }

  getAvailableAnimations() {
    if (!this.transitionSpine?.skeleton?.data?.animations) return [];
    return this.transitionSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
  }

  stopTransition() {
    if (this.transitionSpine && this.isPlaying) {
      this.transitionSpine.state?.clearListeners();
      this.transitionSpine.state?.clearTracks();
      this.transitionSpine.alpha = 0.001;
      this.isPlaying = false;
      this.isVisible = false;
    }
  }

  cleanup() {
    if (this._frontMaskRetryTimeout) {
      clearTimeout(this._frontMaskRetryTimeout);
      this._frontMaskRetryTimeout = null;
    }

    if (this.transitionSpine) {
      this.transitionSpine.state?.clearListeners();
      this.transitionSpine.state?.clearTracks();
      if (
        this.container &&
        this.container.children.includes(this.transitionSpine)
      ) {
        this.container.removeChild(this.transitionSpine);
      }
      this.transitionSpine.destroy();
      this.transitionSpine = null;
    }

    if (this.frontMaskSpine) {
      this.frontMaskSpine.state?.clearListeners();
      this.frontMaskSpine.state?.clearTracks();
      if (
        this.container &&
        this.container.children.includes(this.frontMaskSpine)
      ) {
        this.container.removeChild(this.frontMaskSpine);
      }
      this.frontMaskSpine.destroy();
      this.frontMaskSpine = null;
    }

    this.isPlaying = false;
    this.isVisible = false;
  }
}

export default FreeSpinSceneTransitionController;
